import React, { useState } from "react";
import { useForm } from "react-hook-form";
import { Switch, Spin, Button, message, Tooltip } from 'antd';
import { DownloadOutlined, CopyOutlined } from '@ant-design/icons';
import { toast } from "../components/Toast/toast";
import { DownloadIcon } from "../components/Icons/iconImages";

type FormValues = {

    data: string;
};

const GoogleAdsQueryValidator: React.FC = () => {
    const [data, setData] = useState<any>([]);
    const [loading, setLoading] = useState(false);
    const [formInput, setFormInput] = useState<string>("");
    const defaultValues = {

        data: ""
    }
    const {
        register,
        handleSubmit,
        reset,
        formState: { errors },
    } = useForm<FormValues>({
        defaultValues
    });

    const onSubmit = async (data: FormValues) => {
        setLoading(true)
        setFormInput(data.data);

        try {
            const response = await fetch(`${window.location.origin}/api/v1/validate-google-ads-query-screen`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                },
                body: data?.data,
            });

            if (!response.ok) {
                // Try to parse error response body
                let errorData;
                try {
                    errorData = await response.json();
                } catch (parseError) {
                    errorData = { error: `HTTP error! status: ${response.status}`, message: response.statusText };
                }

                // Show error in the raw response box
                setData({
                    error: true,
                    status: response.status,
                    statusText: response.statusText,
                    ...errorData
                });
                toast.error(errorData?.message || errorData?.error || "Failed to validate Google Ads query. Please try again.");
                setLoading(false);
                return;
            }

            const responseData = await response.json();
            setData(responseData);
            setLoading(false);
        } catch (error: any) {
            console.error("Error:", error);
            // Show error details in the raw response box
            setData({
                error: true,
                message: error?.message || "Network error occurred",
                details: error?.toString(),
                timestamp: new Date().toISOString()
            });
            toast.error("Failed to validate Google Ads query. Please try again.");
            setLoading(false);
        }
    };

    const handleDownloadCsv = async () => {
        try {
            const response = await fetch(`${window.location.origin}/api/v1/download-google-ads-query-csv`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                },
                body: formInput,
            });

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const blob = await response.blob();
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `google-ads-query-${Date.now()}.csv`;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            window.URL.revokeObjectURL(url);

            toast.success('CSV file downloaded successfully!');
        } catch (error) {
            console.error('Download error:', error);
            toast.error('Failed to download CSV file. Please try again.');
        }
    };

    const handleCopyToClipboard = async () => {
        try {
            await navigator.clipboard.writeText(JSON.stringify(data, null, 2));
            toast.success('Results copied to clipboard!');
        } catch (error) {
            console.error('Copy error:', error);
            toast.error('Failed to copy to clipboard. Please try again.');
        }
    };

    const handleClear = () => {
        reset();
        setData([]);
        setFormInput("");
        toast.success('Form cleared!');
    };

    return (

        <div className="grid grid-cols-2 border mt-3">
            <form onSubmit={handleSubmit(onSubmit)} style={{ display: "flex", flexDirection: "column", gap: "1rem", padding: 10, borderRight: "1px solid grey" }}>
                <div>
                <label>
                  Google ads Query Validator Data Object
                </label>
                    <textarea  {...register("data", { required: "Data is required" })} style={{border:"1px solid",width:"100%",minHeight:"200px"}}/>
                    {errors.data && <p className="text-red-500 text-sm">{errors.data.message}</p>}
                </div>
 

                <div className="flex gap-2">
                    <button type="submit" className="bg-[#4C8067] h-11 text-white font-semibold flex-1" disabled={loading}>{loading ? "Loading" : "Submit"}{loading && <Spin style={{ color: "#FFFFFF" }} />}</button>
                    <button type="button" onClick={handleClear} className="bg-gray-500 h-11 text-white font-semibold px-4">Clear</button>
                </div>
            </form>
            <div className="p-3">
                <div className="flex justify-between items-center mb-3">
                    <p>Results:</p>
                    {data && Object.keys(data).length > 0 && !loading && !data.error && (
                        <div className="flex gap-2">
                            <Tooltip title="Copy to clipboard">
                                <Button
                                    onClick={handleCopyToClipboard}
                                    // type="text"
                                    shape="circle"
                                    icon={<CopyOutlined />}
                                    className="hover:bg-gray-100"
                                />
                            </Tooltip>
                            <Tooltip title="Download CSV">
                                <Button
                                    onClick={handleDownloadCsv}
                                    // type="text"
                                    shape="circle"
                                    icon={<DownloadIcon />}
                                    // className="hover:bg-gray-100"
                                />
                            </Tooltip>
                        </div>
                    )}
                    {data && Object.keys(data).length > 0 && !loading && data.error && (
                        <div className="flex gap-2">
                            <Tooltip title="Copy error to clipboard">
                                <Button
                                    onClick={handleCopyToClipboard}
                                    // type="text"
                                    shape="circle"
                                    icon={<CopyOutlined />}
                                    className="hover:bg-gray-100"
                                />
                            </Tooltip>
                        </div>
                    )}
                </div>
                {
                    loading ? (
                        <div className="flex justify-center items-center">
                            <Spin size="large" />
                        </div>
                    ) : (
                        <pre className={`text-sm text-wrap p-3 rounded ${data?.error ? 'bg-red-50 border border-red-300 text-red-700' : 'bg-gray-50'}`}>
                            {JSON.stringify(data, null, 2)}
                        </pre>
                    )
                }
            </div>
        </div>
    );
};

export default GoogleAdsQueryValidator;
